/**
 * Subscription client wrapper around WebSocketClient
 * Provides high-level subscription methods for real-time data
 */

import { WebSocketClient } from "../../transport/websocket/WebSocketClient";
import { fromSatoshi } from "../../utils/math";
import {
  SubscriptionClientConfig,
  SubscriptionCallback,
  ConnectionState,
} from "../../types";
import { MultiplexClient } from "./MultiplexClient";

export class SubscriptionClient {
  private wsClient: WebSocketClient;
  private subscriptions: Map<string, SubscriptionCallback[]> = new Map();
  private lastPriceWrappedCallbacks: Map<
    SubscriptionCallback,
    SubscriptionCallback
  > = new Map();
  private wrappedCallbacks: Map<
    string,
    Map<SubscriptionCallback, SubscriptionCallback>
  > = new Map();
  // Multiplex client for multi-feed subscriptions (shares the same WebSocketClient)
  private multiplexClient: MultiplexClient;

  constructor(config: SubscriptionClientConfig) {
    this.wsClient = new WebSocketClient({
      url: config.url,
      authToken: config.authToken,
      walletAddress: config.walletAddress,
      deviceID: config.deviceID,
    });
    // Create MultiplexClient that shares the same WebSocketClient instance
    this.multiplexClient = new MultiplexClient(this.wsClient);
  }

  /**
   * Connect to WebSocket server
   */
  async connect(): Promise<void> {
    await this.wsClient.connect();
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.wsClient.disconnect();
    this.subscriptions.clear();
    this.lastPriceWrappedCallbacks.clear();
    this.wrappedCallbacks.clear();
    // Clean up multiplex client subscriptions
    this.multiplexClient.disconnect();
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.wsClient.isConnected();
  }

  /**
   * Get connection state
   */
  getState(): ConnectionState {
    return this.wsClient.getState();
  }

  /**
   * Register a callback for connection state changes
   * Returns an unsubscribe function
   */
  onStateChange(handler: (state: ConnectionState) => void): () => void {
    return this.wsClient.onStateChange(handler);
  }

  /**
   * Set authentication token, address, and device ID
   * Mirrors SetAuthToken from ws_manager.go
   * Delegates to WebSocketClient.setAuthToken
   */
  setAuthToken(token: string, address: string, deviceID: string): void {
    this.wsClient.setAuthToken(token, address, deviceID);
  }

  /**
   * Update authentication credentials
   * Now async to await reconnection
   * Delegates to WebSocketClient.updateAuth
   */
  async updateAuth(
    authToken?: string,
    walletAddress?: string,
    deviceID?: string,
  ): Promise<void> {
    return this.wsClient.updateAuth(authToken, walletAddress, deviceID);
  }

  /**
   * Check if ready for subscriptions (connected and authenticated if required)
   * Delegates to WebSocketClient.isReadyForSubscriptions
   */
  isReadyForSubscriptions(): boolean {
    return this.wsClient.isReadyForSubscriptions();
  }

  /**
   * Get current authentication state
   * Delegates to WebSocketClient.getAuthState
   */
  getAuthState(): "pending" | "authenticated" | "failed" | "not_required" {
    return this.wsClient.getAuthState();
  }

  /**
   * Wait for subscription acknowledgment
   * Delegates to WebSocketClient.waitForSubscription
   *
   * @param channel - Channel name to wait for
   * @param timeoutMs - Timeout in milliseconds (default: 5000)
   */
  async waitForSubscription(
    channel: string,
    timeoutMs: number = 5000,
  ): Promise<void> {
    return this.wsClient.waitForSubscription(channel, timeoutMs);
  }

  /**
   * Get the WebSocketClient instance
   * This allows other clients (like SubscriptionExplorer) to share the same WebSocket connection
   * @returns The WebSocketClient instance
   */
  getWebSocketClient(): WebSocketClient {
    return this.wsClient;
  }

  /**
   * Helper to parse router data field (can be JSON string or object)
   */
  private parseRouterData(data: any): any {
    if (typeof data === "string") {
      try {
        return JSON.parse(data);
      } catch (e) {
        console.error("Failed to parse router data field:", e);
        return null;
      }
    }
    return data;
  }

  /**
   * Helper to extract and parse data from response
   * Supports both action-based protocol (primary) and Router protocol (backward compatibility)
   */
  private extractRouterData(message: any): any | null {
    // Handle action-based protocol: { action: "data" | "update", channel: "...", data: {...} } or direct data
    if (typeof message.action === "string") {
      if (
        (message.action === "data" || message.action === "update") &&
        message.data
      ) {
        const parsed = this.parseRouterData(message.data);
        if (!parsed) {
          console.warn(
            "[SubscriptionClient] Failed to parse action-based data:",
            message.data,
          );
        }
        return parsed;
      }
      // For action-based protocol, data might be directly in the message
      if (message.action === "data" || message.action === "update") {
        // If no data field, return the message itself (minus action/channel)
        const { action, channel, ...data } = message;
        return Object.keys(data).length > 0 ? data : null;
      }
      // Handle error responses
      if (message.action === "error") {
        if (message.error) {
          console.error("WebSocket error response:", message.error);
        } else if (message.message) {
          console.error("WebSocket error response:", message.message);
        }
        return null;
      }
      // Handle subscribed acknowledgment (silently ignore)
      if (message.action === "subscribed") {
        return null;
      }
    }

    // Handle Router protocol (primary): { type: "data", channel: "...", data: "..." } or { type: "data", channel: "...", data: {...} }
    if (message.type === "data" && message.data) {
      const parsed = this.parseRouterData(message.data);
      if (!parsed) {
        console.warn(
          "[SubscriptionClient] Failed to parse router data:",
          message.data,
        );
      }
      return parsed;
    }
    // Handle error responses
    if (message.type === "error") {
      if (message.error) {
        console.error("WebSocket error response:", message.error);
      } else if (message.message) {
        console.error("WebSocket error response:", message.message);
      }
      return null;
    }
    // Handle ack responses (silently ignore)
    if (message.type === "ack") {
      return null;
    }

    // For action-based protocol, if message has channel but no action/type, it might be direct data
    if (message.channel && !message.action && !message.type) {
      const { channel, ...data } = message;
      return Object.keys(data).length > 0 ? data : null;
    }

    // Log unexpected message format
    if (
      message.action &&
      message.action !== "data" &&
      message.action !== "update" &&
      message.action !== "subscribed" &&
      message.action !== "error"
    ) {
      console.debug(
        "[SubscriptionClient] Unexpected action:",
        message.action,
        "Full message:",
        message,
      );
    } else if (
      message.type &&
      message.type !== "data" &&
      message.type !== "ack" &&
      message.type !== "error"
    ) {
      console.debug(
        "[SubscriptionClient] Unexpected message type:",
        message.type,
        "Full message:",
        message,
      );
    } else if (
      (message.action === "data" ||
        message.action === "update" ||
        message.type === "data") &&
      !message.data &&
      !message.channel
    ) {
      console.warn(
        "[SubscriptionClient] Message is data/update but missing data/channel field:",
        message,
      );
    }
    return null;
  }

  /**
   * Parameter validation methods
   */
  private validateSymbol(symbol: string): boolean {
    // Skip validation if symbol is empty string
    if (symbol === "") {
      return true;
    }
    if (!symbol || typeof symbol !== "string" || symbol.trim() === "") {
      return false;
    }
    // Basic validation: should contain "/" separator (e.g., "BTC/USDC")
    return symbol.includes("/");
  }

  private validateMarketId(marketId: string): boolean {
    if (marketId === null || marketId === undefined) {
      return false;
    }
    // Ensure it's a string
    const marketIdStr = String(marketId).trim();
    // Check it's not empty and not "0" (which might be invalid)
    // Note: "0" might be valid for some markets, but typically we want non-zero market IDs
    return marketIdStr !== "";
  }

  private validateKlineInterval(interval: string): boolean {
    const validIntervals = ["1m", "5m", "15m", "1h", "4h", "1d"];
    return validIntervals.includes(interval);
  }

  private validateTWAPWindowSize(windowSize: number): boolean {
    return (
      typeof windowSize === "number" &&
      windowSize > 0 &&
      Number.isFinite(windowSize)
    );
  }

  private validatePrivateChannelAuth(): void {
    // Check if ready for subscriptions (connected and authenticated if required)
    if (!this.isReadyForSubscriptions()) {
      const authState = this.getAuthState();
      if (authState === "pending") {
        throw new Error(
          "Authentication is pending. Please wait for authentication to complete before subscribing to private channels.",
        );
      } else if (authState === "failed") {
        throw new Error(
          "Authentication failed. Please re-authenticate before subscribing to private channels.",
        );
      } else {
        // Get auth token from wsClient config
        const wsClientConfig = (this.wsClient as any).config;
        if (!wsClientConfig || !wsClientConfig.authToken) {
          throw new Error(
            "Authentication token is required for private channels. Please provide authToken in SubscriptionClient config.",
          );
        }
      }
    }
  }

  /**
   * ============================================================================
   * Multi-Feed Subscription Methods (delegated to MultiplexClient)
   * ============================================================================
   */

  /**
   * Get the MultiplexClient instance for direct access to multi-feed methods
   * @returns The MultiplexClient instance that shares the same WebSocketClient
   */
  getMultiplexClient(): MultiplexClient {
    return this.multiplexClient;
  }

  /**
   * Subscribe to multi-feed channel
   * Multi-feed provides a single subscription that streams updates from multiple sub-channels
   * Delegates to MultiplexClient
   * @param marketId - Market ID to subscribe to (string). Use empty string "" for auth-only sub-channels like balances, orders, positions, buckets
   * @param callback - Callback function for multi-feed updates (receives { sub_channel, data })
   * @param params - Optional object with custom extra parameters (e.g., { symbol: "BTC/USDC", interval: "1m" } for kline)
   */
  subscribeMultiFeed(
    marketId: string,
    callback: SubscriptionCallback,
    params?: Record<string, any>,
  ): void {
    return this.multiplexClient.subscribeMultiFeed(marketId, callback, params);
  }

  /**
   * Subscribe to a specific sub-channel within multi-feed
   * This is a convenience method that filters multi-feed messages for a specific sub-channel
   * Delegates to MultiplexClient
   * @param subChannel - Sub-channel name (e.g., "balances", "orders", "positions", "orderbook", etc.)
   * @param marketId - Market ID to subscribe to (string). Use empty string "" for auth-only sub-channels like "balances", "orders", "positions", "buckets" - these only require authentication
   * @param callback - Callback function for sub-channel updates
   * @param params - Optional object with custom extra parameters for different sub channels (e.g., { interval: "1m" } for kline, { symbol: "BTC/USDC" } if needed)
   */
  subscribeMultiFeedSubChannel(
    subChannel: string,
    marketId: string,
    callback: SubscriptionCallback,
    params?: Record<string, any>,
  ): void {
    return this.multiplexClient.subscribeMultiFeedSubChannel(
      subChannel,
      marketId,
      callback,
      params,
    );
  }

  /**
   * Unsubscribe from multi-feed channel
   * Delegates to MultiplexClient
   */
  unsubscribeMultiFeed(
    _marketId: string,
    _symbol: string,
    callback?: SubscriptionCallback,
  ): void {
    return this.multiplexClient.unsubscribeMultiFeed(_marketId, _symbol, callback);
  }

  /**
   * Unsubscribe from a specific multi-feed sub-channel
   * Delegates to MultiplexClient
   */
  unsubscribeMultiFeedSubChannel(
    subChannel: string,
    callback?: SubscriptionCallback,
  ): void {
    return this.multiplexClient.unsubscribeMultiFeedSubChannel(subChannel, callback);
  }

  /**
   * ============================================================================
   * Individual Channel Subscription Methods
   * ============================================================================
   */

  /**
   * Subscribe to orderbook updates
   * @param marketId - Market ID to subscribe to (string or number)
   * @param callback - Callback function for orderbook updates
   * @param convertToDecimal - Whether to convert satoshi to decimal (default: false)
   *                           Note: Conversion is typically handled by the consumer (e.g., useOrderbook hook)
   */
  subscribeOrderbook(
    marketId: string,
    callback: SubscriptionCallback,
    convertToDecimal: boolean = false,
  ): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    // Validate parameters
    if (!this.validateMarketId(marketId)) {
      throw new Error(
        `Invalid market ID: ${marketId}. Market ID must be a non-empty string.`,
      );
    }

    const channel = "orderbook";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    // Wrap callback to handle action-based protocol format
    // Note: Conversion is handled by the consumer (e.g., useOrderbook hook)
    const wrappedCallback = (message: any) => {
      // Extract and parse data from response
      const orderbookData = this.extractRouterData(message);
      if (!orderbookData) return;

      // Convert satoshi to decimal only if explicitly requested
      // By default, pass raw satoshi data to consumer for conversion
      if (
        convertToDecimal &&
        orderbookData.bids &&
        Array.isArray(orderbookData.bids)
      ) {
        orderbookData.bids = orderbookData.bids
          .filter(
            (entry: any): entry is [string | number, string | number] =>
              Array.isArray(entry) && entry.length >= 2,
          )
          .map(([price, size]: [string | number, string | number]) => [
            fromSatoshi(typeof price === "string" ? price : String(price)),
            fromSatoshi(typeof size === "string" ? size : String(size)),
          ]);
      }
      if (
        convertToDecimal &&
        orderbookData.asks &&
        Array.isArray(orderbookData.asks)
      ) {
        orderbookData.asks = orderbookData.asks
          .filter(
            (entry: any): entry is [string, string] =>
              Array.isArray(entry) && entry.length >= 2,
          )
          .map(([price, size]: [string, string]) => [
            fromSatoshi(price),
            fromSatoshi(size),
          ]);
      }
      callback(orderbookData);
    };

    // Store wrapped callback for proper cleanup
    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    // Register handler on channel name
    this.wsClient.on(channel, wrappedCallback);

    // Register pending subscription for acknowledgment tracking
    this.wsClient.registerPendingSubscription(channel);

    // Send subscription message using Router protocol format (type with params)
    try {
      const subscribeMsg = {
        type: "subscribe",
        channel: "orderbook",
        params: {
          market_id: marketId,
        },
      };
      console.debug(
        "[SubscriptionClient] Sending orderbook subscription:",
        subscribeMsg,
      );
      this.wsClient.send(subscribeMsg);
      // Optionally wait for subscription acknowledgment (non-blocking)
      // Users can call waitForSubscription() if they need to wait
    } catch (error) {
      // Clean up on send failure
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from orderbook updates
   */
  unsubscribeOrderbook(
    _marketId: string,
    callback?: SubscriptionCallback,
  ): void {
    const channel = "orderbook";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        // Remove wrapped callback
        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      // Remove all callbacks
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    // Send unsubscribe message if no more callbacks and connected
    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "orderbook",
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to trades
   */
  subscribeTrades(marketId: string, callback: SubscriptionCallback): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    // Validate parameters
    if (!this.validateMarketId(marketId)) {
      throw new Error(
        `Invalid market ID: ${marketId}. Market ID must be a non-empty string.`,
      );
    }

    const channel = "trades";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    // Wrap callback to handle action-based protocol format
    const wrappedCallback = (message: any) => {
      const tradesData = this.extractRouterData(message);
      if (tradesData) {
        callback(tradesData);
      }
    };

    // Store wrapped callback for proper cleanup
    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    // Register handler on channel name
    this.wsClient.on(channel, wrappedCallback);

    // Send subscription message using Router protocol format
    try {
      this.wsClient.send({
        type: "subscribe",
        channel: "trades",
        params: {
          market_id: marketId,
        },
      });
    } catch (error) {
      // Clean up on send failure
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from trades
   */
  unsubscribeTrades(_marketId: string, callback?: SubscriptionCallback): void {
    const channel = "trades";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        // Remove wrapped callback
        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      // Remove all callbacks
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "trades",
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to ticker updates (moniker channel)
   * Note: Router uses "moniker" channel name, but we keep "ticker" in the API for consistency
   */
  subscribeTicker(marketId: string, callback: SubscriptionCallback): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    // Validate parameters
    if (!this.validateMarketId(marketId)) {
      throw new Error(
        `Invalid market ID: ${marketId}. Market ID must be a non-empty string.`,
      );
    }

    // Router protocol uses "moniker" channel name, but we track as "ticker" internally
    const routerChannel = "moniker";
    const internalChannel = "ticker";
    this.subscriptions.set(internalChannel, [
      ...(this.subscriptions.get(internalChannel) || []),
      callback,
    ]);

    // Wrap callback to handle Router protocol format
    // WebSocketClient now routes type="data" messages by channel name, so we can register directly on channel
    const wrappedCallback = (message: any) => {
      const tickerData = this.extractRouterData(message);
      if (tickerData) {
        callback(tickerData);
      }
    };

    // Store wrapped callback for proper cleanup
    if (!this.wrappedCallbacks.has(internalChannel)) {
      this.wrappedCallbacks.set(internalChannel, new Map());
    }
    this.wrappedCallbacks.get(internalChannel)!.set(callback, wrappedCallback);

    // Register handler on router channel name (WebSocketClient routes type="data" messages by channel)
    this.wsClient.on(routerChannel, wrappedCallback);

    // Send subscription message using Router protocol format
    try {
      this.wsClient.send({
        type: "subscribe",
        channel: routerChannel,
        params: {
          market_id: marketId,
        },
      });
    } catch (error) {
      // Clean up on send failure
      this.wrappedCallbacks.get(internalChannel)?.delete(callback);
      const callbacks = this.subscriptions.get(internalChannel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(internalChannel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from ticker updates
   */
  unsubscribeTicker(_marketId: string, callback?: SubscriptionCallback): void {
    const internalChannel = "ticker";
    const routerChannel = "moniker";
    const callbacks = this.subscriptions.get(internalChannel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(internalChannel, callbacks);

        // Remove wrapped callback
        const wrappedCallback = this.wrappedCallbacks
          .get(internalChannel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(routerChannel, wrappedCallback);
          this.wrappedCallbacks.get(internalChannel)!.delete(callback);
        }
      }
    } else {
      // Remove all callbacks
      const channelWrappedCallbacks =
        this.wrappedCallbacks.get(internalChannel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(routerChannel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(internalChannel);
      }
      this.subscriptions.delete(internalChannel);
    }

    if (
      this.subscriptions.get(internalChannel)?.length === 0 &&
      this.isConnected()
    ) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: routerChannel,
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to user positions updates
   * Requires authentication (walletAddress must be provided)
   */
  subscribeUserUpdates(callback: SubscriptionCallback): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    // Validate authentication for private channel
    this.validatePrivateChannelAuth();

    // Use "positions" channel which requires address parameter
    const channel = "positions";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    // Wrap callback to handle action-based protocol format
    const wrappedCallback = (message: any) => {
      const positionsData = this.extractRouterData(message);
      if (positionsData) {
        callback(positionsData);
      }
    };

    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    // Register handler on channel name
    this.wsClient.on(channel, wrappedCallback);

    // Register pending subscription for acknowledgment tracking
    this.wsClient.registerPendingSubscription(channel);

    // Get auth token from wsClient config
    const wsClientConfig = (this.wsClient as any).config;
    const authToken = wsClientConfig?.authToken;

    // Send subscription message using Router protocol format
    // Positions channel uses address from authenticated connection
    try {
      const subscribeMsg: Record<string, any> = {
        type: "subscribe",
        channel: "positions",
        params: {},
      };
      // Note: auth_token is typically in connection URL, but can be in params if needed
      if (authToken) {
        subscribeMsg.params.auth_token = authToken;
      }
      this.wsClient.send(subscribeMsg);
      // Optionally wait for subscription acknowledgment:
      // await this.waitForSubscription(channel);
    } catch (error) {
      // Clean up on send failure
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from user updates
   */
  unsubscribeUserUpdates(callback?: SubscriptionCallback): void {
    const channel = "positions";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "positions",
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to last price updates
   * @param marketId - Market ID to subscribe to (string or number)
   * @param callback - Callback function for last price updates
   * @param convertToDecimal - Whether to convert satoshi to decimal (default: true)
   */
  subscribeLastPrice(
    marketId: string,
    callback: SubscriptionCallback,
    convertToDecimal: boolean = true,
  ): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    // Validate parameters
    if (!this.validateMarketId(marketId)) {
      throw new Error(
        `Invalid market ID: ${marketId}. Market ID must be a non-empty string.`,
      );
    }

    const channel = "last_price";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    // Wrap callback to handle decimal conversion and Router protocol format
    // WebSocketClient now routes type="data" messages by channel name, so we can register directly on channel
    const wrappedCallback = (message: any) => {
      console.debug(
        "[SubscriptionClient] last_price message received:",
        message,
      );
      const tradeData = this.extractRouterData(message);
      if (!tradeData) {
        console.warn(
          "[SubscriptionClient] Failed to extract trade data from message:",
          message,
        );
        return;
      }

      console.debug("[SubscriptionClient] Extracted trade data:", tradeData);

      // Validate required fields
      if (!tradeData.price) {
        console.warn(
          "[SubscriptionClient] Trade data missing price field:",
          tradeData,
        );
        return;
      }

      // Convert price and quantity from satoshi strings to decimal numbers
      if (convertToDecimal) {
        if (tradeData.price) {
          tradeData.price = fromSatoshi(
            typeof tradeData.price === "string"
              ? tradeData.price
              : String(tradeData.price),
          );
        }
        if (tradeData.quantity) {
          tradeData.quantity = fromSatoshi(
            typeof tradeData.quantity === "string"
              ? tradeData.quantity
              : String(tradeData.quantity),
          );
        }
      }

      console.debug(
        "[SubscriptionClient] Calling callback with trade data:",
        tradeData,
      );
      callback(tradeData);
    };

    // Store wrapped callback for proper cleanup
    this.lastPriceWrappedCallbacks.set(callback, wrappedCallback);

    // Register handler on channel name (WebSocketClient routes type="data" messages by channel)
    this.wsClient.on(channel, wrappedCallback);

    // Send subscription message using Router protocol format
    try {
      this.wsClient.send({
        type: "subscribe",
        channel: "last_price",
        params: {
          market_id: marketId,
        },
      });
    } catch (error) {
      // Clean up on send failure
      this.lastPriceWrappedCallbacks.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from last price updates
   */
  unsubscribeLastPrice(
    _marketId: string,
    callback?: SubscriptionCallback,
  ): void {
    const channel = "last_price";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        // Remove wrapped callback
        const wrappedCallback = this.lastPriceWrappedCallbacks.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.lastPriceWrappedCallbacks.delete(callback);
        }
      }
    } else {
      // Remove all callbacks
      callbacks.forEach((cb) => {
        const wrappedCallback = this.lastPriceWrappedCallbacks.get(cb);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.lastPriceWrappedCallbacks.delete(cb);
        }
      });
      this.subscriptions.delete(channel);
    }

    // Send unsubscribe message if no more callbacks and connected
    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "last_price",
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to funding rate updates
   * @param marketId - Market ID to subscribe to (string or number)
   * @param callback - Callback function for funding rate updates
   */
  subscribeFundingRate(marketId: string, callback: SubscriptionCallback): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    // Validate parameters
    if (!this.validateMarketId(marketId)) {
      throw new Error(
        `Invalid market ID: ${marketId}. Market ID must be a non-empty string.`,
      );
    }

    const channel = "funding_rate";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    // Wrap callback to handle action-based protocol format
    const wrappedCallback = (message: any) => {
      const fundingRateData = this.extractRouterData(message);
      if (fundingRateData) {
        callback(fundingRateData);
      }
    };

    // Store wrapped callback for proper cleanup
    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    // Register handler on channel name
    this.wsClient.on(channel, wrappedCallback);

    // Send subscription message using Router protocol format
    try {
      this.wsClient.send({
        type: "subscribe",
        channel: "funding_rate",
        params: {
          market_id: marketId,
        },
      });
    } catch (error) {
      // Clean up on send failure
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from funding rate updates
   * @param marketId - Market ID to unsubscribe from (string or number)
   * @param callback - Optional callback to remove specific subscription
   */
  unsubscribeFundingRate(
    _marketId: string,
    callback?: SubscriptionCallback,
  ): void {
    const channel = "funding_rate";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        // Remove wrapped callback
        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      // Remove all callbacks
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    // Send unsubscribe message if no more callbacks and connected
    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "funding_rate",
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to buckets (user's margin buckets)
   * Requires authentication (walletAddress must be provided)
   * @param callback - Callback function for buckets updates
   */
  subscribeBuckets(callback: SubscriptionCallback): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    // Validate authentication for private channel
    this.validatePrivateChannelAuth();

    const channel = "buckets";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    const wrappedCallback = (message: any) => {
      const bucketsData = this.extractRouterData(message);
      if (bucketsData) {
        callback(bucketsData);
      }
    };

    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    this.wsClient.on(channel, wrappedCallback);

    // Register pending subscription for acknowledgment tracking
    this.wsClient.registerPendingSubscription(channel);

    // Get auth token from wsClient config
    const wsClientConfig = (this.wsClient as any).config;
    const authToken = wsClientConfig?.authToken;

    // Send subscription message using Router protocol format
    try {
      const subscribeMsg: Record<string, any> = {
        type: "subscribe",
        channel: "buckets",
        params: {},
      };
      // Note: auth_token is typically in connection URL, but can be in params if needed
      if (authToken) {
        subscribeMsg.params.auth_token = authToken;
      }
      this.wsClient.send(subscribeMsg);
      // Optionally wait for subscription acknowledgment:
      // await this.waitForSubscription(channel);
    } catch (error) {
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from buckets updates
   */
  unsubscribeBuckets(callback?: SubscriptionCallback): void {
    const channel = "buckets";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "buckets",
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to deviation updates (price deviation between perp and spot)
   * @param marketId - Market ID to subscribe to (string or number)
   * @param callback - Callback function for deviation updates
   */
  subscribeDeviation(marketId: string, callback: SubscriptionCallback): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    // Validate parameters
    if (!this.validateMarketId(marketId)) {
      throw new Error(
        `Invalid market ID: ${marketId}. Market ID must be a non-empty string.`,
      );
    }

    const channel = "deviation";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    const wrappedCallback = (message: any) => {
      const deviationData = this.extractRouterData(message);
      if (deviationData) {
        callback(deviationData);
      }
    };

    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    this.wsClient.on(channel, wrappedCallback);

    // Send subscription message using Router protocol format
    try {
      this.wsClient.send({
        type: "subscribe",
        channel: "deviation",
        params: {
          market_id: marketId,
        },
      });
    } catch (error) {
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from deviation updates
   */
  unsubscribeDeviation(
    _marketId: string,
    callback?: SubscriptionCallback,
  ): void {
    const channel = "deviation";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "deviation",
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to index price updates (oracle index price)
   * @param marketId - Market ID to subscribe to (string or number)
   * @param callback - Callback function for index price updates
   */
  subscribeIndexPrice(marketId: string, callback: SubscriptionCallback): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    // Validate parameters
    if (!this.validateMarketId(marketId)) {
      throw new Error(
        `Invalid market ID: ${marketId}. Market ID must be a non-empty string.`,
      );
    }

    const channel = "index_price";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    const wrappedCallback = (message: any) => {
      const indexPriceData = this.extractRouterData(message);
      if (indexPriceData) {
        callback(indexPriceData);
      }
    };

    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    this.wsClient.on(channel, wrappedCallback);

    // Send subscription message using Router protocol format
    try {
      this.wsClient.send({
        type: "subscribe",
        channel: "index_price",
        params: {
          market_id: marketId,
        },
      });
    } catch (error) {
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from index price updates
   */
  unsubscribeIndexPrice(
    _marketId: string,
    callback?: SubscriptionCallback,
  ): void {
    const channel = "index_price";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "index_price",
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to kline (OHLC candlestick) updates
   * @param marketId - Market ID to subscribe to (string or number)
   * @param interval - Interval for kline data (required: "1m", "5m", "15m", "1h", "4h", "1d")
   * @param callback - Callback function for kline updates
   */
  subscribeKline(
    marketId: string,
    interval: string,
    callback: SubscriptionCallback,
  ): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    // Validate parameters
    if (!this.validateMarketId(marketId)) {
      throw new Error(
        `Invalid market ID: ${marketId}. Market ID must be a non-empty string.`,
      );
    }
    if (!this.validateKlineInterval(interval)) {
      throw new Error(
        `Invalid interval: ${interval}. Interval must be one of: "1m", "5m", "15m", "1h", "4h", "1d".`,
      );
    }

    const channel = "kline";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    const wrappedCallback = (message: any) => {
      const klineData = this.extractRouterData(message);
      if (klineData) {
        callback(klineData);
      }
    };

    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    this.wsClient.on(channel, wrappedCallback);

    // Send subscription message using Router protocol format
    try {
      this.wsClient.send({
        type: "subscribe",
        channel: "kline",
        params: {
          market_id: marketId,
          interval: interval,
        },
      });
    } catch (error) {
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from kline updates
   */
  unsubscribeKline(
    _marketId: string,
    _interval: string,
    callback?: SubscriptionCallback,
  ): void {
    const channel = "kline";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "kline",
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to mark price updates (mark price for liquidations)
   * @param marketId - Market ID to subscribe to (string or number)
   * @param callback - Callback function for mark price updates
   */
  subscribeMarkPrice(marketId: string, callback: SubscriptionCallback): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    // Validate parameters
    if (!this.validateMarketId(marketId)) {
      throw new Error(
        `Invalid market ID: ${marketId}. Market ID must be a non-empty string.`,
      );
    }

    const channel = "mark_price";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    const wrappedCallback = (message: any) => {
      const markPriceData = this.extractRouterData(message);
      if (markPriceData) {
        callback(markPriceData);
      }
    };

    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    this.wsClient.on(channel, wrappedCallback);

    // Send subscription message using Router protocol format
    try {
      this.wsClient.send({
        type: "subscribe",
        channel: channel,
        params: {
          market_id: marketId,
        },
      });
    } catch (error) {
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from mark price updates
   */
  unsubscribeMarkPrice(
    _marketId: string,
    callback?: SubscriptionCallback,
  ): void {
    const channel = "mark_price";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "mark_price",
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to price feed updates (oracle price feeds)
   * @param marketId - Market ID to subscribe to (string or number)
   * @param callback - Callback function for price feed updates
   */
  subscribePriceFeed(marketId: string, callback: SubscriptionCallback): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    // Validate parameters
    if (!this.validateMarketId(marketId)) {
      throw new Error(
        `Invalid market ID: ${marketId}. Market ID must be a non-empty string.`,
      );
    }

    const channel = "price_feed";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    const wrappedCallback = (message: any) => {
      const priceFeedData = this.extractRouterData(message);
      if (priceFeedData) {
        callback(priceFeedData);
      }
    };

    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    this.wsClient.on(channel, wrappedCallback);

    // Send subscription message using Router protocol format
    try {
      this.wsClient.send({
        type: "subscribe",
        channel: "price_feed",
        params: {
          market_id: marketId,
        },
      });
    } catch (error) {
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from price feed updates
   */
  unsubscribePriceFeed(
    _marketId: string,
    callback?: SubscriptionCallback,
  ): void {
    const channel = "price_feed";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "price_feed",
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to TWAP (Time-Weighted Average Price) updates
   * @param marketId - Market ID to subscribe to (string or number)
   * @param windowSize - Time window in seconds (e.g., 300 for 5 minutes)
   * @param callback - Callback function for TWAP updates
   */
  subscribeTWAP(
    marketId: string,
    windowSize: number,
    callback: SubscriptionCallback,
  ): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    // Validate parameters
    if (!this.validateMarketId(marketId)) {
      throw new Error(
        `Invalid market ID: ${marketId}. Market ID must be a non-empty string.`,
      );
    }
    if (!this.validateTWAPWindowSize(windowSize)) {
      throw new Error(
        `Invalid window size: ${windowSize}. Window size must be a positive number.`,
      );
    }

    const channel = "twap";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    const wrappedCallback = (message: any) => {
      const twapData = this.extractRouterData(message);
      if (twapData) {
        callback(twapData);
      }
    };

    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    this.wsClient.on(channel, wrappedCallback);

    // Send subscription message using Router protocol format
    try {
      this.wsClient.send({
        type: "subscribe",
        channel: "twap",
        params: {
          market_id: marketId,
          window_size: windowSize,
        },
      });
    } catch (error) {
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from TWAP updates
   */
  unsubscribeTWAP(
    _marketId: string,
    _windowSize: number,
    callback?: SubscriptionCallback,
  ): void {
    const channel = "twap";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "twap",
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to balances updates (authenticated channel)
   * Requires authentication (walletAddress must be provided)
   * @param callback - Callback function for balances updates
   */
  subscribeBalances(callback: SubscriptionCallback): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    // Validate authentication for private channel
    this.validatePrivateChannelAuth();

    const channel = "balances";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    const wrappedCallback = (message: any) => {
      const balancesData = this.extractRouterData(message);
      if (balancesData) {
        callback(balancesData);
      }
    };

    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    this.wsClient.on(channel, wrappedCallback);

    // Register pending subscription for acknowledgment tracking
    this.wsClient.registerPendingSubscription(channel);

    // Get auth token from wsClient config
    const wsClientConfig = (this.wsClient as any).config;
    const authToken = wsClientConfig?.authToken;

    // Send subscription message using Router protocol format
    try {
      const subscribeMsg: Record<string, any> = {
        type: "subscribe",
        channel: "balances",
        params: {},
      };
      // Note: auth_token is typically in connection URL, but can be in params if needed
      if (authToken) {
        subscribeMsg.params.auth_token = authToken;
      }
      this.wsClient.send(subscribeMsg);
      // Optionally wait for subscription acknowledgment:
      // await this.waitForSubscription(channel);
    } catch (error) {
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from balances updates
   */
  unsubscribeBalances(callback?: SubscriptionCallback): void {
    const channel = "balances";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "balances",
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to orders updates (authenticated channel)
   * Requires authentication (walletAddress must be provided)
   * @param callback - Callback function for orders updates
   */
  subscribeOrders(callback: SubscriptionCallback): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    // Validate authentication for private channel
    this.validatePrivateChannelAuth();

    const channel = "orders";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    const wrappedCallback = (message: any) => {
      const ordersData = this.extractRouterData(message);
      if (ordersData) {
        callback(ordersData);
      }
    };

    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    this.wsClient.on(channel, wrappedCallback);

    // Register pending subscription for acknowledgment tracking
    this.wsClient.registerPendingSubscription(channel);

    // Get auth token from wsClient config
    const wsClientConfig = (this.wsClient as any).config;
    const authToken = wsClientConfig?.authToken;

    // Send subscription message using Router protocol format
    try {
      const subscribeMsg: Record<string, any> = {
        type: "subscribe",
        channel: "orders",
        params: {},
      };
      // Note: auth_token is typically in connection URL, but can be in params if needed
      if (authToken) {
        subscribeMsg.params.auth_token = authToken;
      }
      this.wsClient.send(subscribeMsg);
      // Optionally wait for subscription acknowledgment:
      // await this.waitForSubscription(channel);
    } catch (error) {
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from orders updates
   */
  unsubscribeOrders(callback?: SubscriptionCallback): void {
    const channel = "orders";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "orders",
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }
}
